import React, { useState, useEffect } from 'react';
import { FileText, Edit, Trash2, Calendar, Search, Download, Eye } from 'lucide-react';
import { User } from '../lib/types';
import { getSavedPosts } from '../lib/auth';

interface SavedPostsProps {
  user: User;
}

const SavedPosts: React.FC<SavedPostsProps> = ({ user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [savedPosts, setSavedPosts] = useState<any[]>([]);
  
  useEffect(() => {
    // Load saved posts when component mounts
    const posts = getSavedPosts();
    setSavedPosts(posts);
  }, []);

  // Filter posts by search term
  const filteredPosts = savedPosts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleDelete = (postId: string) => {
    // Filter out the deleted post
    const updatedPosts = savedPosts.filter(post => post.id !== postId);
    setSavedPosts(updatedPosts);
    
    // Update localStorage
    localStorage.setItem('saved_posts', JSON.stringify(updatedPosts));
  };

  const handleExport = (post: any) => {
    // Create a blob with the post content
    const blob = new Blob([post.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = `${post.title}-${formatDate(post.date)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Mes offres d'emploi sauvegardées</h2>
      
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Rechercher une offre..."
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {filteredPosts.length > 0 ? (
        <div className="space-y-4">
          {filteredPosts.map(post => (
            <div key={post.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{post.title}</h3>
                  <p className="text-indigo-600">{post.company}</p>
                </div>
                <div className="flex items-center space-x-2 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{formatDate(post.date)}</span>
                </div>
              </div>
              
              <p className="text-gray-600 mt-2 line-clamp-2">
                {post.content.substring(0, 200)}...
              </p>
              
              <div className="flex justify-end mt-4 space-x-3">
                <button 
                  onClick={() => {/* Implement view functionality */}}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  <span>Voir</span>
                </button>
                <button 
                  onClick={() => {/* Implement edit functionality */}}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>Modifier</span>
                </button>
                <button 
                  onClick={() => handleExport(post)}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Exporter</span>
                </button>
                <button 
                  onClick={() => handleDelete(post.id)}
                  className="flex items-center space-x-1 text-gray-600 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucune offre trouvée</h3>
          <p className="text-gray-500">
            {searchTerm 
              ? "Aucune offre ne correspond à votre recherche" 
              : "Vous n'avez pas encore sauvegardé d'offres d'emploi"}
          </p>
          {!searchTerm && (
            <button 
              onClick={() => window.location.href = '/'}
              className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
            >
              Générer une offre
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedPosts;